import { Portfolio } from "@/types/Portfolio";

export type NewsquawkCategory =
  | "Indices"
  | "Technology"
  | "Energy"
  | "Financials"
  | "Industrials"
  | "Healthcare"
  | "Consumer"
  | "Materials"
  | "Utilities"
  | "Real Estate"
  | "Communications"
  | "Macro"
  // Asset-class labels. Sectors above describe equity lists; these cover the
  // rest of the book, and are what the desk actually picks — see
  // ASSET_CLASS_CATEGORY below.
  | "Equities"
  | "FX"
  | "Fixed Income"
  | "Metals"
  | "Crypto"
  | "Other";

export interface CategoryConfig {
  id: NewsquawkCategory;
  label: string;
  description: string;
  defaultCollapsed?: boolean;
}

export const NEWSQUAWK_CATEGORIES: CategoryConfig[] = [
  {
    id: "Indices",
    label: "Indices",
    description: "Major market indices",
    defaultCollapsed: false,
  },
  {
    id: "Technology",
    label: "Technology",
    description: "Technology sector watchlists",
    defaultCollapsed: false,
  },
  {
    id: "Energy",
    label: "Energy",
    description: "Energy sector watchlists",
    defaultCollapsed: false,
  },
  {
    id: "Financials",
    label: "Financials",
    description: "Financial sector watchlists",
    defaultCollapsed: false,
  },
  {
    id: "Industrials",
    label: "Industrials",
    description: "Industrial sector watchlists",
    defaultCollapsed: false,
  },
  {
    id: "Healthcare",
    label: "Healthcare",
    description: "Healthcare sector watchlists",
    defaultCollapsed: false,
  },
  {
    id: "Consumer",
    label: "Consumer",
    description: "Consumer sector watchlists",
    defaultCollapsed: false,
  },
  {
    id: "Materials",
    label: "Materials",
    description: "Materials sector watchlists",
    defaultCollapsed: false,
  },
  {
    id: "Utilities",
    label: "Utilities",
    description: "Utilities sector watchlists",
    defaultCollapsed: false,
  },
  {
    id: "Real Estate",
    label: "Real Estate",
    description: "Real estate sector watchlists",
    defaultCollapsed: false,
  },
  {
    id: "Communications",
    label: "Communications",
    description: "Communications sector watchlists",
    defaultCollapsed: false,
  },
  {
    id: "Macro",
    label: "Macro",
    description: "Economic and macro watchlists",
    defaultCollapsed: false,
  },
  {
    id: "Equities",
    label: "Equities",
    description: "Equity watchlists",
    defaultCollapsed: false,
  },
  {
    id: "FX",
    label: "FX",
    description: "Currency watchlists",
    defaultCollapsed: false,
  },
  {
    id: "Fixed Income",
    label: "Fixed Income",
    description: "Rates and bond watchlists",
    defaultCollapsed: false,
  },
  {
    id: "Metals",
    label: "Metals",
    description: "Precious and base metals watchlists",
    defaultCollapsed: false,
  },
  {
    id: "Crypto",
    label: "Crypto",
    description: "Digital asset watchlists",
    defaultCollapsed: false,
  },
  {
    id: "Other",
    label: "Other",
    description: "General watchlists",
    defaultCollapsed: true,
  },
];

/**
 * The vocabulary the desk picks from, stored on the record as `categories`.
 *
 * Deliberately the asset classes a customer chooses during onboarding
 * (`src/onboarding/profileMapping.ts`) rather than the equity sectors above:
 * a desk label then feeds the onboarding watchlist step and the concierge
 * directly, with no translation table to keep in step.
 */
export const DESK_CATEGORY_OPTIONS = [
  { id: "equities", label: "Equities" },
  { id: "fx", label: "FX" },
  { id: "fixed_income", label: "Fixed Income" },
  { id: "energy", label: "Energy" },
  { id: "metals", label: "Metals" },
  { id: "crypto", label: "Crypto" },
  { id: "macro", label: "Macro" },
  { id: "all", label: "All / cross-asset" },
] as const;

export type DeskCategoryId = (typeof DESK_CATEGORY_OPTIONS)[number]["id"];

/** Stored id -> the label the library groups under. */
const ASSET_CLASS_CATEGORY: Record<string, NewsquawkCategory> = {
  equities: "Equities",
  fx: "FX",
  fixed_income: "Fixed Income",
  energy: "Energy",
  metals: "Metals",
  crypto: "Crypto",
  macro: "Macro",
  // Cross-asset lists have no single home; they group under Other but are
  // not *uncategorised* — the desk said "everything", and the desk view
  // distinguishes the two.
  all: "Other",
};

// Pattern-based category inference from watchlist names
// These patterns will be matched case-insensitively against watchlist names
const CATEGORY_PATTERNS: { category: NewsquawkCategory; patterns: RegExp[] }[] = [
  {
    category: "Indices",
    patterns: [
      /s&p\s*500/i,
      /spx/i,
      /spy/i,
      /nasdaq/i,
      /qqq/i,
      /dow\s*jones/i,
      /djia/i,
      /dia/i,
      /russell/i,
      /iwm/i,
      /nyse/i,
      /index/i,
      /indices/i,
      /composite/i,
      /ftse/i,
      /dax/i,
      /cac/i,
      /nikkei/i,
      /hang\s*seng/i,
      /stoxx/i,
      /asx/i,
      /tsx/i,
    ],
  },
  {
    category: "Technology",
    patterns: [
      /tech(nology)?/i,
      /semiconductor/i,
      /software/i,
      /xlk/i,
      /qqq/i,
      /smh/i,
    ],
  },
  {
    category: "Energy",
    patterns: [
      /energy/i,
      /oil/i,
      /gas\b/i,
      /xle/i,
      /xop/i,
      /uso/i,
    ],
  },
  {
    category: "Financials",
    patterns: [
      /bank/i,
      /broker/i,
      /lender/i,
      /insurer/i,
      /financ(e|ial)/i,
      /banking/i,
      /bank\b/i,
      /insurance/i,
      /xlf/i,
      /kbe/i,
      /kre/i,
    ],
  },
  {
    category: "Industrials",
    patterns: [
      /industrial/i,
      /transport/i,
      /airline/i,
      /aerospace/i,
      /defense/i,
      /xli/i,
      /iyt/i,
    ],
  },
  {
    category: "Healthcare",
    patterns: [
      /healthcare/i,
      /health\s*care/i,
      /pharma/i,
      /bio(tech)?/i,
      /medical/i,
      /xlv/i,
      /xbi/i,
      /ibb/i,
    ],
  },
  {
    category: "Consumer",
    patterns: [
      /consumer/i,
      /retail/i,
      /discretionary/i,
      /staples/i,
      /xly/i,
      /xlp/i,
      /xrt/i,
    ],
  },
  {
    category: "Materials",
    patterns: [
      /material/i,
      /mining/i,
      /metal/i,
      /steel/i,
      /gold/i,
      /silver/i,
      /copper/i,
      /commodit/i,
      /xlb/i,
      /gdx/i,
      /gld/i,
    ],
  },
  {
    category: "Utilities",
    patterns: [
      /utilit(y|ies)/i,
      /xlu/i,
    ],
  },
  {
    category: "Real Estate",
    patterns: [
      /real\s*estate/i,
      /reit/i,
      /xlre/i,
      /vnq/i,
    ],
  },
  {
    category: "Communications",
    patterns: [
      /communication/i,
      /telecom/i,
      /media/i,
      /entertainment/i,
      /xlc/i,
    ],
  },
  {
    category: "Macro",
    patterns: [
      /geopolit/i,
      /tariff/i,
      /trade\s*war/i,
      /sanction/i,
      /administration/i,
      /fed(eral)?/i,
      /fomc/i,
      /\brate/i,
      /inflation/i,
      /gdp/i,
      /cpi/i,
      /ppi/i,
      /employment/i,
      /jobs/i,
      /nfp/i,
      /payroll/i,
      /central\s*bank/i,
      /ecb/i,
      /boe/i,
      /boj/i,
      /monetary/i,
      /fiscal/i,
      /treasur/i,
      /bond/i,
      /yield/i,
      /debt/i,
      /deficit/i,
      /stimulus/i,
      /quantitative/i,
      /hawkish/i,
      /dovish/i,
      /recession/i,
      /economic/i,
      /macro/i,
    ],
  },
];

/**
 * Which group a Newsquawk watchlist browses under.
 *
 * Three sources, in order of how much we trust them:
 *
 *  1. A sector matched from the name. Kept first because it is finer than an
 *     asset class — forty equity lists collapsed into one "Equities" bucket is
 *     worse browsing than Technology / Financials / Healthcare, and the desk
 *     picks asset classes, not sectors.
 *  2. The desk's own `categories`. This is what kills the lazy "Other": a list
 *     named "Aramco Suppliers" matches no sector pattern, but the desk said
 *     energy, so it groups under Energy instead of the junk drawer.
 *  3. "Other", now reached only by a list nobody has labelled and whose name
 *     says nothing — which is a real state worth seeing, not a default.
 */
export function categorizeWatchlist(portfolio: Portfolio): NewsquawkCategory {
  const name = portfolio.name;

  for (const { category, patterns } of CATEGORY_PATTERNS) {
    for (const pattern of patterns) {
      if (pattern.test(name)) {
        return category;
      }
    }
  }

  for (const id of portfolio.categories ?? []) {
    const mapped = ASSET_CLASS_CATEGORY[id];
    if (mapped) return mapped;
  }

  return "Other";
}

/** Has the desk said what this covers? Drives the desk view's backfill count. */
export function isCategorised(record: {
  categories?: string[] | null;
}): boolean {
  return (record.categories ?? []).length > 0;
}

/**
 * Group an array of watchlists by their inferred category.
 * Returns an object with category keys and arrays of watchlists.
 */
export function groupWatchlistsByCategory(
  watchlists: Portfolio[]
): Record<NewsquawkCategory, Portfolio[]> {
  // Built from the vocabulary rather than a literal: adding a category used to
  // mean remembering to add a key here and another in getCategoryCounts.
  const grouped = Object.fromEntries(
    NEWSQUAWK_CATEGORIES.map((c) => [c.id, [] as Portfolio[]])
  ) as Record<NewsquawkCategory, Portfolio[]>;

  for (const watchlist of watchlists) {
    const category = categorizeWatchlist(watchlist);
    grouped[category].push(watchlist);
  }

  // Sort each category by priority
  for (const category of Object.keys(grouped) as NewsquawkCategory[]) {
    grouped[category].sort((a, b) => a.priority - b.priority);
  }

  return grouped;
}

/**
 * Get the count of watchlists in each category.
 */
export function getCategoryCounts(
  watchlists: Portfolio[]
): Record<NewsquawkCategory, number> {
  const grouped = groupWatchlistsByCategory(watchlists);
  return Object.fromEntries(
    Object.entries(grouped).map(([id, items]) => [id, items.length])
  ) as Record<NewsquawkCategory, number>;
}

/**
 * Get categories that have at least one watchlist.
 */
export function getNonEmptyCategories(
  watchlists: Portfolio[]
): CategoryConfig[] {
  const counts = getCategoryCounts(watchlists);
  return NEWSQUAWK_CATEGORIES.filter((cat) => counts[cat.id] > 0);
}
